import React, { useCallback, useContext, useMemo } from 'react';
import SphConstants from 'sg-sph-constants';
import CommonConstants from 'sg-common-constants';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { SgProductService, SgSessionStorageService } from 'saga-modules';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import SgSessionTimeoutError from 'sg-session-timeout-error';
import SpmConstants from 'sg-spm-constants';
import { Button, Link } from '@jutro/components';
import SmiConstants from 'sg-smi-constants';
import SgSptConstants from 'sg-spt-constants';
import messages from './SgCommonErrorMessages.messages';

function SgCommonError(props) {
    const {
        errorCodeT,
        errorCodeC,
        quoteID,
        location,
        history
    } = props;

    const {
        SG_ERROR_CODES,
        SAGA_PHONE_NUMBERS
    } = CommonConstants;

    const {
        isHome,
        isQuickQuote,
        isSmi,
        isTravel
    } = appConfig;

    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';

    const {
        productName,
        phoneNumber,
        paymentPhoneNumber,
        linesOpenParagraph,
        getQuoteLink,
        insuranceInfoLink
    } = useMemo(() => {
        if (isHome || SgProductService.isQuickQuoteHome()) {
            return {
                productName: SphConstants.HOME,
                phoneNumber: SAGA_PHONE_NUMBERS.SAGA_ERROR_HOME_PHONE_NUMBER,
                paymentPhoneNumber: SAGA_PHONE_NUMBERS.SAGA_ERROR_PAYMENT_HOME_PHONE_NUMBER,
                linesOpenParagraph: messages.LinesOpen,
                getQuoteLink: CommonConstants.SAGA_LINKS.HOME_GET_QUOTE,
                insuranceInfoLink: CommonConstants.SAGA_LINKS.HOME_INSURANCE_INFO
            };
        }
        if (isSmi) {
            return {
                productName: SmiConstants.SAGA_PRIVATE_HEALTH,
                phoneNumber: SAGA_PHONE_NUMBERS.SAGA_SMI_PHONE_NUMBER,
                linesOpenParagraph: messages.LinesOpenSmi,
                getQuoteLink: CommonConstants.SAGA_LINKS.PMI_GET_QUOTE,
                insuranceInfoLink: CommonConstants.SAGA_LINKS.PMI_INSURANCE_INFO
            };
        }
        if (isTravel) {
            return {
                productName: SgSptConstants.SAGA_TRAVEL_INSURANCE,
                phoneNumber: SAGA_PHONE_NUMBERS.SAGA_TRAVEL_PHONE_NUMBER,
                linesOpenParagraph: messages.LinesOpenTravel,
                getQuoteLink: CommonConstants.SAGA_LINKS.TRAVEL_GET_QUOTE,
                insuranceInfoLink: CommonConstants.SAGA_LINKS.TRAVEL_INSURANCE_INFO
            };
        }
        return {
            productName: SgProductService.isMotorHome() ? SpmConstants.MOTORHOME : SpmConstants.CAR,
            phoneNumber: SAGA_PHONE_NUMBERS.SAGA_ERROR_MOTOR_PHONE_NUMBER,
            paymentPhoneNumber: SAGA_PHONE_NUMBERS.SAGA_ERROR_PAYMENT_MOTOR_PHONE_NUMBER,
            linesOpenParagraph: messages.LinesOpen,
            getQuoteLink: SgProductService.isMotorHome() ? CommonConstants.SAGA_LINKS.MOTORHOME_GET_QUOTE
                : CommonConstants.SAGA_LINKS.MOTOR_GET_QUOTE,
            insuranceInfoLink: SgProductService.isMotorHome() ? CommonConstants.SAGA_LINKS.MOTORHOME_INSURANCE_INFO
                : CommonConstants.SAGA_LINKS.MOTOR_INSURANCE_INFO
        };
    }, [isHome, isSmi, isTravel, SAGA_PHONE_NUMBERS]);

    const headerContent = useMemo(() => {
        switch (errorCodeT) {
            case SG_ERROR_CODES.SAGA_SESSION_TIMEOUT:// T2
                return isQuickQuote ? translator(messages.HeaderTimeoutQuickQuote, { productName: productName })
                    : translator(messages.HeaderTimeout, { productName: productName });
            case SG_ERROR_CODES.SAGA_UPDATING_WEBSITE: // T3
            case SG_ERROR_CODES.SAGA_WEBSITE_ERROR: // T4
            case SG_ERROR_CODES.SAGA_QUOTATION_BIND_ERROR: // T7
            case SG_ERROR_CODES.SAGA_PC_CONNECTION_ERROR: // T14
                return translator(messages.ErrorHeader);
            case SG_ERROR_CODES.SAGA_RETRIEVING_AMENDED_OFFLINE: // T13
            case SG_ERROR_CODES.SAGA_QUOTATION_ERROR: // T6
                return translator(messages.ErrorHeaderTouched);
            case SG_ERROR_CODES.BARCLAYCARD_FRAUDULENT_CARD: // T8
            case SG_ERROR_CODES.SAGA_PAYMENT_FAILED: // T10
            case SG_ERROR_CODES.SAGA_PAYMENT_NO_PAYMENT: // T11
            case SG_ERROR_CODES.SAGA_BARCLAYCARD_REVIEW_RETRY: // T16
                return translator(messages.ErrorHeaderPayment);
            case SG_ERROR_CODES.SAGA_WRONG_URL_ERROR: // T404
                return translator(messages.WrongUrlHeader);
            default:
                return translator(messages.ErrorHeader);
        }
    }, [errorCodeT, productName, isQuickQuote, translator, SG_ERROR_CODES]);

    const beginNewQuoteContent = useMemo(() => {
        let newQuoteHref = '/journey';
        if (isQuickQuote) {
            const id = SgSessionStorageService.getItem(SgSessionStorageService.KEY.QQ_BASE_ID);
            const productCode = SgProductService.isQuickQuoteHome() ? CommonConstants.HOME_PRODUCT_CODE : CommonConstants.MOTOR_PRODUCT_CODE;
            newQuoteHref = `/new-quickQuote?id=${id}&product=${productCode}`;
        }
        return (
            <div>
                <Link to={newQuoteHref} className="sgErrorPageNewQuoteLink">
                    {translator(messages.NewQuote)}
                </Link>
            </div>
        );
    }, [isQuickQuote, translator]);

    const renderGenericError = useMemo(() => {
        return (
            <>
                <p className="sgErrorPagePrimaryText">{translator(messages.SomethingGoneWrong)}</p>
                <span className="sgErrorPageSecondaryText">
                    {translator(messages.PleaseCallUs, {
                        phoneNumber: isMobile ? <a href={`tel:${phoneNumber.replaceAll(' ', '')}`}>{phoneNumber}</a> : <strong>{phoneNumber}</strong>
                    })}
                </span>
                <p className="sgErrorPageLinesOpen">{translator(linesOpenParagraph)}</p>
            </>
        );
    }, [translator, isMobile, phoneNumber, linesOpenParagraph]);

    const renderPaymentMessagePurchaseError = useCallback(() => {
        return (
            <>
                <p className="sgErrorPagePrimaryText">{translator(messages.PurchaseNotComplete)}</p>
                <p className="sgErrorPageSecondaryText">
                    {translator(messages.PaymentCallUs, {
                        phoneNumber: isMobile ? <a href={`tel:${paymentPhoneNumber.replaceAll(' ', '')}`}>{paymentPhoneNumber}</a>
                            : paymentPhoneNumber
                    })}
                </p>
            </>
        );
    }, [translator, isMobile, paymentPhoneNumber]);

    const goToPayment = useCallback(() => {
        const wizardLink = '/journey';
        SgSessionStorageService.setItem(SgSessionStorageService.KEY.LAST_URL, '/payment');
        SgSessionStorageService.setItem(SgSessionStorageService.KEY.PAYMENT_RETRIEVAL, true);
        SgSessionStorageService.setItem(SgSessionStorageService.KEY.IS_RELOADING, true);
        history.push(wizardLink);
    }, [history]);

    const renderPaymentNotTakenError = useCallback(() => {
        return (
            <>
                <p className="sgErrorPagePrimaryText">{translator(messages.PaymentProblem)}</p>
                <p className="sgErrorPageSecondaryText">{translator(messages.PaymentTryAgain)}</p>
                <Button type="button" onClick={goToPayment} className="sgButton sgPrimaryButton">
                    {translator(messages.ReturnToPayment)}
                </Button>
            </>
        );
    }, [goToPayment, translator]);

    const renderPaymentPendingError = useCallback(() => {
        return (
            <>
                <p className="sgErrorPagePrimaryText">{translator(messages.PaymentProblem)}</p>
                <p className="sgErrorPageSecondaryText">
                    {translator(messages.PaymentDiscussFurther, {
                        phoneNumber: isMobile
                            ? (
                                <a href={`tel:${SAGA_PHONE_NUMBERS.SAGA_ERROR_PAYMENT_COMMON_PHONE_NUMBER.replaceAll(' ', '')}`}>
                                    {SAGA_PHONE_NUMBERS.SAGA_ERROR_PAYMENT_COMMON_PHONE_NUMBER}
                                </a>
                            ) : SAGA_PHONE_NUMBERS.SAGA_ERROR_PAYMENT_COMMON_PHONE_NUMBER
                    })}
                </p>
                <p className="sgErrorPageSecondaryText">{translator(messages.PaymentPending)}</p>
                <p className="sgErrorPageSecondaryText">{translator(messages.PaymentLinesOpen)}</p>
                <p className="sgErrorPageSecondaryText">{translator(messages.PaymentOpeningTimes)}</p>
            </>
        );
    }, [translator, isMobile, SAGA_PHONE_NUMBERS.SAGA_ERROR_PAYMENT_COMMON_PHONE_NUMBER]);

    const wrongUrlErrorContent = useMemo(() => { // for T404
        return (
            <>
                <p>{translator(messages.WeCantFind)}</p>
                <p>{translator(messages.LostPage)}</p>
                <ul className="sgMarkerList">
                    <li id="quoteEngineFirstPage">
                        <div className="sgMarkerListItem" />
                        <Link
                            id="quoteEngineFirstPageLink"
                            href={getQuoteLink}
                        >
                            {translator(messages.GetQuote, {
                                product: productName
                            })}
                        </Link>
                    </li>
                    <li id="productPage">
                        <div className="sgMarkerListItem" />
                        <Link
                            id="productPageLink"
                            href={insuranceInfoLink}
                        >
                            {translator(messages.FindOutAboutInsurance, {
                                product: productName
                            })}
                        </Link>
                    </li>
                    <li id="sagaInsuranceMainPage">
                        <div className="sgMarkerListItem" />
                        <Link
                            id="sagaInsuranceMainPageLink"
                            href={CommonConstants.SAGA_LINKS.SAGA_INSURANCE_URL}
                        >
                            {translator(messages.AllSagaInsurance)}
                        </Link>
                    </li>
                </ul>
            </>
        );
    }, [getQuoteLink, insuranceInfoLink, productName, translator]);

    const messageContent = useMemo(() => {
        switch (errorCodeT) {
            case SG_ERROR_CODES.SAGA_SESSION_TIMEOUT: // T2
                if (errorCodeC && errorCodeC === SG_ERROR_CODES.SAGA_SESSION_BLOCKED_ERROR) {
                    return translator(messages.ErrorRetrievalBlockedMessage, {
                        phoneNumber: isMobile
                            ? (
                                <a href={`tel:${SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER.replaceAll(' ', '')}`}>
                                    {SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER}
                                </a>
                            ) : SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER
                    });
                }
                return (
                    <SgSessionTimeoutError
                        errorCodeT={errorCodeT}
                        errorCodeC={errorCodeC}
                        quoteID={quoteID}
                        location={location}
                        history={history}
                        phoneNumber={phoneNumber}
                    />
                );
            case SG_ERROR_CODES.SAGA_UPDATING_WEBSITE: // T3
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">{translator(messages.PageUnavailableMessage)}</p>
                        <p className="sgErrorPageSecondaryText">{translator(messages.CallUsUnavailable)}</p>
                        {isMobile
                            ? (
                                <a
                                    className="sgErrorPagePhoneNumber"
                                    href={`tel:${SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER.replaceAll(' ', '')}`}
                                >
                                    {SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER}
                                </a>
                            ) : (
                                <p className="sgErrorPagePhoneNumber">{SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER}</p>
                            )}
                        <p className="sgErrorPageLinesOpen">{translator(messages.LinesOpen)}</p>
                    </>
                );
            case SG_ERROR_CODES.SAGA_WEBSITE_ERROR: // T4
                return (
                    <>
                        {renderGenericError}
                        {beginNewQuoteContent}
                    </>
                );
            case SG_ERROR_CODES.SAGA_QUOTATION_BIND_ERROR: // T7
            case SG_ERROR_CODES.SAGA_PC_CONNECTION_ERROR: // T14
                return renderGenericError;
            case SG_ERROR_CODES.SAGA_RETRIEVING_AMENDED_OFFLINE: // T13
                return (
                    <p className="sgErrorPagePrimaryText">
                        {translator(messages.TouchedOfflineMessage, {
                            phoneNumber: isMobile
                                ? (
                                    <a href={`tel:${SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER.replaceAll(' ', '')}`}>
                                        {SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER}
                                    </a>
                                ) : SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_COMMON_DEFAULT_PHONE_NUMBER
                        })}
                    </p>
                );
            case SG_ERROR_CODES.SAGA_PAYMENT_NO_PAYMENT: // T11
            case SG_ERROR_CODES.SAGA_PAYMENT_FAILED: // T10
                return renderPaymentMessagePurchaseError();
            case SG_ERROR_CODES.BARCLAYCARD_FRAUDULENT_CARD: { // T8
                return renderPaymentPendingError();
            }
            case SG_ERROR_CODES.SAGA_BARCLAYCARD_REVIEW_RETRY: // T16
                return renderPaymentNotTakenError();
            case SG_ERROR_CODES.SAGA_QUOTATION_ERROR: // T6
                return (<p className="sgErrorPagePrimaryText">{translator(messages.QuotationErrorMessage)}</p>);
            case SG_ERROR_CODES.SAGA_WRONG_URL_ERROR: // T404
                return wrongUrlErrorContent;
            default:
                return renderGenericError;
        }
    }, [errorCodeT, errorCodeC, translator, isMobile, SG_ERROR_CODES, SAGA_PHONE_NUMBERS, history, location, phoneNumber, quoteID, renderGenericError,
        wrongUrlErrorContent, beginNewQuoteContent, renderPaymentNotTakenError, renderPaymentPendingError, renderPaymentMessagePurchaseError]);

    return (
        <div>
            <h1>{headerContent}</h1>
            <div>
                {messageContent}
            </div>
        </div>
    );
}

export default SgCommonError;
